/*
 * Project: bellcloud
 * File: packages/ui/components/common/Modal.tsx
 * Description: Reusable styled modal components + ModalShell
 */

import React from 'react';
import styled from '@emotion/styled';

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${props => (props.isOpen ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  z-index: ${({ theme }) => theme.zIndex.modal};
  backdrop-filter: blur(2px);
`;

const ModalContent = styled.div<{ maxWidth?: string; maxHeight?: string }>`
  background: ${({ theme }) => theme.colors.surface.layout.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg}px;
  padding: ${({ theme }) => theme.spacing[7]}px;
  width: 90%;
  max-width: ${props => props.maxWidth || '560px'};
  max-height: ${props => props.maxHeight || '80vh'};
  overflow-y: auto;
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[4]}px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xl}px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ModalTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]}px;
  min-width: 0;
`;

export const ModalBody = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
  margin-bottom: ${({ theme }) => theme.spacing[4]}px;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]}px;
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing[6]}px;
`;

const ModalCloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: ${({ theme }) => theme.fontSize.xl}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

// ──────────────────────────────────────────────
// ModalShell: 모든 모달의 공통 껍데기
// ──────────────────────────────────────────────

const SIZE_MAP = {
  sm: '400px',
  md: '560px',
  lg: '720px',
  xl: '1200px',
} as const;

interface ModalShellProps {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  /** 모달 너비 프리셋: sm=400 / md=560(기본) / lg=720 / xl=1200 */
  size?: keyof typeof SIZE_MAP;
  /** 최대 높이 오버라이드 (기본: 80vh) */
  maxHeight?: string;
  /** 하단 버튼 영역 슬롯 */
  footer?: React.ReactNode;
  children: React.ReactNode;
  /** 우상단 X 버튼 표시 여부 (기본: true) */
  showCloseButton?: boolean;
  /** 제목 오른쪽에 붙는 슬롯 (가이드 ? 아이콘 등) */
  titleAddon?: React.ReactNode;
}

export const ModalShell: React.FC<ModalShellProps> = ({
  isOpen,
  title,
  onClose,
  size = 'md',
  maxHeight,
  footer,
  children,
  showCloseButton = true,
  titleAddon,
}) => {
  if (!isOpen) return null;

  return (
    <ModalOverlay isOpen={isOpen}>
      <ModalContent
        maxWidth={SIZE_MAP[size]}
        maxHeight={maxHeight}
      >
        <ModalHeader>
          <ModalTitleGroup>
            <ModalTitle>{title}</ModalTitle>
            {titleAddon}
          </ModalTitleGroup>
          {showCloseButton && (
            <ModalCloseButton onClick={onClose} aria-label="Close">
              ✕
            </ModalCloseButton>
          )}
        </ModalHeader>

        {children}

        {footer && <ModalFooter>{footer}</ModalFooter>}
      </ModalContent>
    </ModalOverlay>
  );
};

// ──────────────────────────────────────────────
// Legacy aliases (하위 호환)
// ──────────────────────────────────────────────

/** @deprecated ModalOverlay로 교체 */
export const SimpleModal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: ${({ theme }) => theme.zIndex.modal};
  padding: ${({ theme }) => theme.spacing[6]}px;
`;
